/**
 * The Survey Map's tree — ONE renderer, read by the sheet and by the
 * `sections` landing page (D-028).
 *
 * Presentational: it takes a `MapModel` and paints it. Rows are anchors —
 * `<a href>` — because this is the app's primary navigation, and the rule
 * the hall indicator carried before it holds: long-press, open in a new tab,
 * and a screen reader's link list all keep working. A branch head is a
 * `<button aria-expanded>`: collapsing a room is UI state, not navigation.
 *
 * Nothing here decides anything. Lamp, the notes count, and which row is
 * `here` all arrive on the model from `mapTree.ts`, the way every lamp in the
 * app reads the gap engine rather than forming its own opinion.
 *
 * D-037: the row is lamp + title. The model still says why a lamp is amber
 * (`reason`), and the map does not print it — "keep this a simple map".
 */

import type { ReactNode } from 'react';

import type { MapBranchModel, MapModel, MapRowModel } from '../state/mapTree';

export interface MapTreeViewProps {
  model: MapModel;
  /** Room ids whose branch is expanded. */
  openBranches: ReadonlySet<string>;
  onToggleBranch: (roomId: string) => void;
  /**
   * Called when a row is tapped — the sheet closes itself and speaks the
   * toast. Navigation is the anchor's own doing; this never preventDefaults.
   */
  onNavigate?: (row: MapRowModel) => void;
  /** Cascade the top-level nodes in — the map has just been born. */
  born?: boolean;
  /** Extra class on the root — the landing page paints it on paper. */
  className?: string;
}

/**
 * The little camera the photo count sits behind. The map row stopped
 * printing a photo count at D-037; `PhotoShelf` is this glyph's reader now.
 */
export function CameraGlyph({ size = 14 }: { size?: number }): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      aria-hidden="true"
    >
      <path d="M3 8h3l2-3h8l2 3h3v12H3z" />
      <circle cx="12" cy="13" r="3.4" />
    </svg>
  );
}

function Row({ row, onNavigate }: { row: MapRowModel; onNavigate?: (row: MapRowModel) => void }): ReactNode {
  return (
    <a
      className={`maprow maprow--${row.lamp}`}
      href={row.href}
      aria-current={row.current ? 'step' : undefined}
      data-done={row.done ? 'true' : undefined}
      onClick={onNavigate ? () => onNavigate(row) : undefined}
    >
      <span className="maprow__lamp" aria-hidden="true" />
      <span className="maprow__body">
        <span className="maprow__title" data-legibility="display">
          {row.title}
        </span>
      </span>
      {row.notes > 0 ? (
        <span className="maprow__notes" data-legibility="display" aria-label={`${String(row.notes)} notes`}>
          ✎{row.notes}
        </span>
      ) : null}
    </a>
  );
}

function Branch({
  branch,
  open,
  onToggle,
  onNavigate,
}: {
  branch: MapBranchModel;
  open: boolean;
  onToggle: () => void;
  onNavigate?: (row: MapRowModel) => void;
}): ReactNode {
  const complete = branch.total > 0 && branch.done === branch.total;
  return (
    <div className={open ? 'branch branch--open' : 'branch'} data-room={branch.roomId}>
      <button
        type="button"
        className="branchhead"
        aria-expanded={open}
        onClick={onToggle}
      >
        <span className="branchhead__twist" aria-hidden="true">
          ▶
        </span>
        <span className="branchhead__name">
          <span className="branchhead__title" data-legibility="display">
            {branch.name}
          </span>
        </span>
        <span
          className={complete ? 'branchhead__stat branchhead__stat--done' : 'branchhead__stat'}
          data-legibility="display"
        >
          {branch.done}/{branch.total}
        </span>
      </button>
      <div className="branch__body">
        <div className="branch__rows">
          {branch.rows.map((row) => (
            <Row key={row.key} row={row} onNavigate={onNavigate} />
          ))}
        </div>
      </div>
    </div>
  );
}

export function MapTreeView({
  model,
  openBranches,
  onToggleBranch,
  onNavigate,
  born = false,
  className,
}: MapTreeViewProps): ReactNode {
  const classes = ['maptree', born ? 'maptree--born' : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <div className={classes}>
      {model.nodes.map((node, index) => {
        const style = born ? { animationDelay: `${String(index * 70)}ms` } : undefined;
        if (node.kind === 'row') {
          return (
            <div key={node.row.key} className="maptree__node" style={style}>
              <Row row={node.row} onNavigate={onNavigate} />
            </div>
          );
        }
        return (
          <div key={node.branch.roomId} className="maptree__node" style={style}>
            <Branch
              branch={node.branch}
              open={openBranches.has(node.branch.roomId)}
              onToggle={() => onToggleBranch(node.branch.roomId)}
              onNavigate={onNavigate}
            />
          </div>
        );
      })}
    </div>
  );
}
